import {addBreadcrumb, addConsoleInstrumentationHandler, captureEvent, captureMessage, checkOrSetAlreadyCaught, getClient, getCurrentScope, getIsolationScope, logger, setUser, type Breadcrumb, type Event, type Integration, type Log} from "@sentry/core";
export {consoleSandbox} from "@sentry/core";

// Public ingestion key, not an administrative token. All services share project 18.
export const glitchTipDsn = "https://c139e35386a748fbb162e80b9d917448@glitchtip.mindcrafters.group/18";

const diagnosticKeys = new Set([
  "service",
  "operation",
  "code",
  "errorCode",
  "errorName",
  "method",
  "route",
  "status",
  "status_code",
  "providerStatus",
  "providerRequestId",
  "providerErrorType",
  "accountCount",
  "frames",
  "lastEvent",
  "idleMs",
  "productId",
  "notificationType",
  "subscriptionStatus",
  "accountMatch",
  "pushType",
  "directive",
  "disposition",
  "line",
  "column",
  "attempt",
  "terminal",
  "jobName",
  "resourceType",
  "release",
  "environment",
  "userId",
  "sessionId",
  "requestId",
  "screen",
  "previousScreen",
  "platform",
  "appVersion",
  "appBuild",
  "clientRelease",
  "durationMs",
  "online",
  "visibility",
  "priorResponses",
  "pageAgeMs",
  "effectiveType",
  "rttMs",
  "saveData",
  "outcome",
  "queryName",
  "mutationName",
  "failureCount",
  "resourceUrl",
  "resourceStatus",
  "resourceBytes",
  "automated",
  "readyState",
  "url",
  "from",
  "to",
  "component",
  "lifecycle",
  "reason",
  "event",
  "eventId",
  "jobId",
  "syncRunId",
  "connectionId",
  "tick",
  "syncedAccounts",
  "syncedTransactions",
  "step",
  "source",
  "provider",
  "plan",
  "channel",
  "method",
  "cold_start",
  "cancelled",
  "control",
  "element",
  "action",
  "count",
  "attempts",
  "retryCount",
  "elapsed_ms",
  "duration_ms",
  "success",
  "active",
  "user.id",
  "sentry.sdk.system",
  "sentry.sdk.identifier",
  "sentry.origin",
  "sentry.trace_id",
  "sentry.span_id",
  "sentry.release",
  "sentry.environment",
  "sentry.sdk.name",
  "sentry.sdk.version",
  "argumentShape"
]);

export type DiagnosticAttributes = Record<string, string | number | boolean>;
export const diagnosticHeaders = {requestId: "x-request-id", sessionId: "x-sprout-session-id", release: "x-sprout-release", screen: "x-sprout-screen"} as const;

export function diagnosticIdentifier(value: unknown): string | undefined {
  return typeof value === "string" && /^[a-zA-Z0-9][a-zA-Z0-9_.:/-]{0,127}$/.test(value) ? value : undefined;
}

/** The same account/session/request dimensions accompany logs and issues. */
export function diagnosticContext(): DiagnosticAttributes {
  const isolation = getIsolationScope().getScopeData();
  const current = getCurrentScope().getScopeData();
  const userId = diagnosticIdentifier(current.user.id ?? isolation.user.id);
  return diagnosticAttributes({...isolation.tags, ...current.tags, ...(userId ? {userId} : {})});
}

export function setDiagnosticUser(userId: string | null): void {
  const id = diagnosticIdentifier(userId);
  setUser(id ? {id} : null);
}

/** Usage is searchable as logs and accompanies subsequent failures as breadcrumbs. */
export function recordDiagnostic(operation: string, attributes: DiagnosticAttributes = {}): void {
  const context = diagnosticContext();
  const data = diagnosticAttributes({...context, ...attributes, operation});
  // Late responses from an old login retain their attribution without entering the new user's trail.
  if (!context.sessionId || !attributes.sessionId || context.sessionId === attributes.sessionId) addBreadcrumb({category: "app", message: operation, level: "info", data});
  logger.info(operation, data);
}

export function redactTelemetryText(value: string): string {
  return value
    .replace(/\b(data|blob):[^\s"'<>]+/gi, "$1:[redacted]")
    .replace(/https?:\/\/[^\s"'<>]+/g, (url) => {
      try {
        const parsed = new URL(url);
        return `${parsed.origin}${parsed.pathname}`;
      } catch {
        return "[url]";
      }
    })
    .replace(/\bBearer\s+\S+/gi, "Bearer [redacted]")
    .replace(/\b[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, "[email]")
    .replace(/\b(?:access[_-]?token|refresh[_-]?token|password|secret|authorization|api[_-]?key)\b["']?\s*[:=]\s*(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s,"'}]+)/gi, "[credential]")
    .slice(0, 2000);
}

export function diagnosticAttributes(value: unknown): Record<string, string | number | boolean> {
  if (!value || typeof value !== "object") return {};
  const result: Record<string, string | number | boolean> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (!diagnosticKeys.has(key)) continue;
    if (typeof entry === "string") result[key] = redactTelemetryText(["url", "resourceUrl", "from", "to", "route"].includes(key) ? (entry.split(/[?#]/)[0] ?? "") : entry);
    else if (typeof entry === "boolean" || (typeof entry === "number" && Number.isFinite(entry))) result[key] = entry;
  }
  return result;
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // UI text and console arguments can contain financial records or chat messages.
  if (breadcrumb.category?.startsWith("ui.") || breadcrumb.category === "console") return null;
  return {...breadcrumb, message: breadcrumb.message ? redactTelemetryText(breadcrumb.message) : "", data: {...diagnosticContext(), ...diagnosticAttributes(breadcrumb.data)}};
}

export function scrubEvent<T extends Event>(event: T): T {
  const userId = diagnosticIdentifier(event.user?.id);
  if (userId) event.user = {id: userId};
  else delete event.user;
  if (event.extra) event.extra = diagnosticAttributes(event.extra);
  if (event.tags) event.tags = diagnosticAttributes(event.tags);
  if (event.tags?.userId === "") delete event.tags.userId;
  if (event.tags?.sessionId && event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.filter((breadcrumb) => !breadcrumb.data?.sessionId || breadcrumb.data.sessionId === event.tags?.sessionId);
  if (event.logentry) event.logentry = {message: redactTelemetryText(event.logentry.message ?? "Application log")};
  if (event.request) {
    // GlitchTip derives the browser and OS contexts from the user agent; every other header can carry credentials.
    const userAgent = event.request.headers?.["User-Agent"] ?? event.request.headers?.["user-agent"];
    event.request = {
      ...(event.request.url ? {url: redactTelemetryText(event.request.url)} : {}),
      ...(event.request.method ? {method: event.request.method} : {}),
      ...(userAgent ? {headers: {"User-Agent": redactTelemetryText(userAgent).slice(0, 512)}} : {})
    };
  }
  // Keep trace/runtime metadata; discard SDK/provider payloads and Vue props.
  if (event.contexts) event.contexts = Object.fromEntries(Object.entries(event.contexts).filter(([key]) => ["trace", "runtime", "browser", "os", "device", "app"].includes(key)));
  if (event.contexts?.app) event.contexts.app = diagnosticAttributes(event.contexts.app);
  if (event.contexts?.trace?.data) event.contexts.trace.data = diagnosticAttributes(event.contexts.trace.data);
  if (event.message) event.message = redactTelemetryText(event.message);
  if (event.exception?.values)
    for (const exception of event.exception.values) {
      if (exception.value) exception.value = redactTelemetryText(exception.value);
      for (const frame of exception.stacktrace?.frames ?? []) {
        delete frame.vars;
        if (frame.filename) frame.filename = redactTelemetryText(frame.filename);
      }
    }
  for (const span of event.spans ?? []) {
    span.description = span.op ?? "operation";
    span.data = diagnosticAttributes(span.data);
  }
  return event;
}

export function scrubLog(log: Log): Log {
  const attributes = diagnosticAttributes({...diagnosticContext(), ...log.attributes});
  if (attributes.userId === "") {
    delete attributes.userId;
    delete attributes["user.id"];
  } else if (typeof attributes.userId === "string") attributes["user.id"] = attributes.userId;
  return {...log, message: redactTelemetryText(String(log.message)), attributes};
}

export function glitchTipOptions(service: string, environment: string, release: string, dsn?: string) {
  return {
    dsn: dsn ?? glitchTipDsn,
    // A local production build has no release; it must not report as production.
    enabled: environment === "production" && release !== "development" && dsn !== "",
    environment,
    release,
    sendDefaultPii: false,
    autoSessionTracking: false,
    sendClientReports: false,
    enableLogs: true,
    sampleRate: 1,
    attachStacktrace: true,
    maxBreadcrumbs: 100,
    // Error and log coverage are unsampled. Tracing is enabled by each runtime.
    tracesSampleRate: 0.01,
    initialScope: {tags: {service}},
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSendLog: (log: Log) => scrubLog({...log, attributes: {...log.attributes, service}})
  };
}

const reportedErrors = new WeakMap<object, {severity: number; attributes: DiagnosticAttributes}>();
export function errorDiagnostics(error: unknown): DiagnosticAttributes {
  return error && typeof error === "object" ? {...reportedErrors.get(error)?.attributes} : {};
}
/** Carries error identity through Pino without serializing its private payload. */
export const diagnosticError = Symbol("diagnosticError");

function diagnosticCode(value: unknown): string | undefined {
  return typeof value === "string" && /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,79}$/.test(value) ? value : undefined;
}

/** SDK rejections (including Plaid Link) are often plain objects, not Error instances. */
function failureAttributes(error: unknown): DiagnosticAttributes {
  if (typeof error === "string" && /^[A-Z][A-Z0-9_]{1,79}$/.test(error)) return {errorCode: error};
  if (!error || typeof error !== "object") return {};
  // AI SDK RetryError keeps every attempt in `errors`; the last one carries the provider status.
  const result: DiagnosticAttributes = {};
  if ("errors" in error && Array.isArray(error.errors) && "lastError" in error && error.lastError !== error) {
    Object.assign(result, failureAttributes(error.lastError), {attempts: error.errors.length});
    const reason = "reason" in error ? diagnosticCode(error.reason) : undefined;
    if (reason) result.reason = reason;
  }
  const code = diagnosticCode("code" in error ? error.code : "error_code" in error ? error.error_code : undefined) ?? (error instanceof Error && /^[A-Z][A-Z0-9_]{1,79}$/.test(error.message) ? error.message : undefined);
  if (code) result.errorCode = code;
  // MCP/JSON-RPC errors carry a negative integer code (e.g. -32603); DOMException's legacy positive codes stay out.
  else if ("code" in error && typeof error.code === "number" && Number.isInteger(error.code) && error.code < 0) result.errorCode = String(error.code);
  if (error instanceof Error) result.errorName = diagnosticCode(error.name) ?? "Error";
  const response = "response" in error && error.response && typeof error.response === "object" ? error.response : error;
  if ("status" in response && typeof response.status === "number" && Number.isInteger(response.status)) result.providerStatus = response.status;
  // AI SDK APICallError and Gateway errors expose the HTTP status as statusCode and a Gateway error class as type.
  if ("statusCode" in error && typeof error.statusCode === "number" && Number.isInteger(error.statusCode)) {
    result.providerStatus = error.statusCode;
    const gatewayType = "type" in error ? diagnosticCode(error.type) : undefined;
    if (gatewayType) result.providerErrorType = gatewayType;
  }
  const provider = "data" in response && response.data && typeof response.data === "object" ? response.data : error;
  if ("error_code" in provider) {
    const providerCode = diagnosticCode(provider.error_code);
    if (providerCode) result.errorCode = providerCode;
  }
  if ("error_type" in provider) {
    const type = diagnosticCode(provider.error_type);
    if (type) result.providerErrorType = type;
  }
  if ("request_id" in provider) {
    const requestId = diagnosticIdentifier(provider.request_id);
    if (requestId) result.providerRequestId = requestId;
  }
  return result;
}

function reportDiagnostic(error: unknown, operation: string, attributes: Record<string, string | number | boolean>, level: "warning" | "error" | "fatal"): void {
  const client = getClient();
  if (!client || client.getOptions().enabled === false) return;
  const attributesWithContext = diagnosticAttributes({...diagnosticContext(), ...failureAttributes(error), ...attributes, operation});
  if (error && typeof error === "object") {
    const severity = level === "fatal" ? 3 : level === "error" ? 2 : 1;
    if ((reportedErrors.get(error)?.severity ?? 0) >= severity) return;
    reportedErrors.set(error, {severity, attributes: attributesWithContext});
    // Global SDK/Vue handlers must not resend the original provider message.
    // Our severity map still permits a retry warning to escalate to an error.
    checkOrSetAlreadyCaught(error);
  }
  // Preserve stack locations without sending provider messages, SQL, or AI input.
  const source = error instanceof Error ? error : new Error(operation);
  const values: NonNullable<Event["exception"]>["values"] = [];
  const seen = new Set<Error>();
  const pending: unknown[] = [source];
  while (pending.length > 0 && values.length < 5) {
    const cause = pending.shift();
    if (!(cause instanceof Error) || seen.has(cause)) continue;
    seen.add(cause);
    const frames = client.getOptions().stackParser?.(cause.stack ?? "");
    const code = failureAttributes(cause).errorCode ?? (cause === source ? attributesWithContext.errorCode : undefined);
    values.unshift({
      type: /^[\w.]{1,80}$/.test(cause.name) ? cause.name : "Error",
      value: code ? `${operation} (${code})` : operation,
      ...(frames?.length ? {stacktrace: {frames}} : {})
    });
    if (cause.cause instanceof Error) pending.unshift(cause.cause);
    if ("lastError" in cause && cause.lastError instanceof Error) pending.unshift(cause.lastError);
    if (cause instanceof AggregateError) {
      const nested: unknown[] = cause.errors;
      pending.push(...nested.slice(0, 5));
    }
  }
  captureEvent({
    level,
    exception: {values},
    ...(typeof attributesWithContext.userId === "string" ? {user: {id: attributesWithContext.userId}} : {}),
    tags: attributesWithContext
  });
}

export function reportError(error: unknown, operation: string, attributes: Record<string, string | number | boolean> = {}): void {
  reportDiagnostic(error, operation, attributes, "error");
}

export function reportWarning(error: unknown, operation: string, attributes: Record<string, string | number | boolean> = {}): void {
  reportDiagnostic(error, operation, attributes, "warning");
}

/**
 * Marks an expected answer, such as a wrong password, as handled at its origin:
 * fallback boundaries (query, mutation, notification) then skip it, while an
 * explicit reportWarning/reportError still files it.
 */
export function markAnswered(error: unknown, operation: string, attributes: DiagnosticAttributes = {}): void {
  if (error && typeof error === "object" && !reportedErrors.has(error)) reportedErrors.set(error, {severity: 0, attributes: diagnosticAttributes({...attributes, operation})});
}

/** Fallback boundaries preserve the severity already chosen by the origin. */
export function reportUnhandledError(error: unknown, operation: string, attributes: DiagnosticAttributes = {}): void {
  if (error && typeof error === "object" && reportedErrors.has(error)) return;
  reportError(error, operation, attributes);
}

/** Node prints process warnings as "(node:<pid>) [CODE] Name: message" through console.error. */
const nodeWarningOutput = /^\(node:\d+\) /;

/** Clarity's tag prints this when its project settings exclude a session, such as a blocked IP; the session is simply not recorded. */
const clarityExcludedSession = "Data from this session is not being collected by Microsoft Clarity due to your configured project settings";
/** TikTok's Pixel prints its own setup and parameter advice with this prefix; it is the pixel's diagnostics, never an app fault. */
const tiktokPixelNotice = "[TikTok Pixel]";

/** Names the types, constructors and key names a console call logged, never their values. */
function consoleArgumentShape(values: readonly unknown[]): string {
  const shapes = values.slice(0, 4).map((value) => {
    if (typeof value === "string") return value ? "string" : "empty-string";
    if (value === null || typeof value !== "object") return value === null ? "null" : typeof value;
    const name: unknown = value.constructor?.name;
    const keys = Object.keys(value)
      .filter((key) => /^[A-Za-z_$][\w$]{0,39}$/.test(key))
      .slice(0, 8);
    return `${typeof name === "string" && /^[\w$]{1,40}$/.test(name) ? name : "Object"}{${keys.join(",")}}`;
  });
  return shapes.join(" ") || "none";
}

/**
 * `expected` marks console output the app already handles, such as a native
 * plugin echoing a user's cancellation; it is logged as a diagnostic, not filed.
 */
export function safeConsoleLoggingIntegration(expected?: (values: readonly unknown[]) => boolean): Integration {
  const levels: Record<string, number> = {debug: 20, info: 30, log: 30, warn: 40, error: 50, assert: 50};
  return {
    name: "SproutConsoleLogs",
    setup(client) {
      const unsubscribe = addConsoleInstrumentationHandler(({args, level}) => {
        const severity = levels[level];
        if (getClient() !== client || severity === undefined) return;
        if (level === "assert" && args[0]) return;
        // SproutNodeWarnings already reports each one as a node.warning with its code.
        if (level === "error" && typeof args[0] === "string" && nodeWarningOutput.test(args[0])) return;
        const values = level === "assert" ? args.slice(1) : args;
        // Filter objects before the SDK can flatten them into the log message.
        const attributes: Record<string, string | number | boolean> = {};
        for (const arg of values) Object.assign(attributes, diagnosticAttributes(arg));
        const vendorNotice = values.includes(clarityExcludedSession) || (typeof values[0] === "string" && values[0].startsWith(tiktokPixelNotice));
        if (severity >= 40 && (vendorNotice || expected?.(values))) {
          recordDiagnostic(`console.${level}.expected`, attributes);
          return;
        }
        const error = values.find((arg): arg is Error => arg instanceof Error);
        const text = values
          .filter((arg) => ["string", "number", "boolean"].includes(typeof arg))
          .map(String)
          .join(" ");
        // A call without text (an object, or an empty string from a native bridge) otherwise arrives as a bare "Console error".
        const shape = text || error ? {} : {argumentShape: consoleArgumentShape(values)};
        forwardLog([{...attributes, ...shape, operation: `console.${level}`, ...(error ? {err: error} : {})}, text || `Console ${level}`], severity);
      });
      client.on("close", unsubscribe);
      client.registerCleanup(unsubscribe);
    }
  };
}

export function forwardLog(args: readonly unknown[], level: number): void {
  const attributes = {...diagnosticContext(), ...diagnosticAttributes(args[0])};
  // Forward structured operational metadata; never serialize arbitrary objects.
  const message = typeof args[0] === "string" ? args[0] : typeof args[1] === "string" ? args[1] : "Application log";
  const severity = level >= 60 ? "fatal" : level >= 50 ? "error" : level >= 40 ? "warn" : level >= 30 ? "info" : level >= 20 ? "debug" : "trace";
  logger[severity](redactTelemetryText(message), attributes);
  if (level >= 40) {
    const object = args[0];
    const candidate = object instanceof Error ? object : object && typeof object === "object" ? (diagnosticError in object ? object[diagnosticError] : "err" in object ? object.err : undefined) : undefined;
    const error = candidate instanceof Error ? candidate : undefined;
    const eventLevel = level >= 60 ? "fatal" : level >= 50 ? "error" : "warning";
    if (error) reportDiagnostic(error, typeof attributes.operation === "string" ? attributes.operation : "logger.error", attributes, eventLevel);
    else captureMessage(redactTelemetryText(message), {level: eventLevel, tags: attributes});
  }
}
